import React, { useMemo, useCallback, useState } from "react";
import Select from "react-select";
import DatePickerPopup from "./DatePickerPopup.js";
import QueueFilter from './QueueFilter'; 

function IntentsFilter({
  filters,
  setFilters,
  intents,
  queues,
  initiationMethods,
  miniView = false,
}) {
  const dateOptions = useMemo(
    () => [
      { value: "today", label: "Today" },
      { value: "thisWeek", label: "This Week" },
      { value: "thisMonth", label: "This Month" },
      { value: "thisYear", label: "This Year" },
      { value: "all", label: "All" },
      { value: "custom", label: "Custom" },
    ],
    []
  );

    const [localDateStart, setLocalDateStart] = useState(new Date());
    const [localDateEnd, setLocalDateEnd] = useState(new Date());
  
    const [showDatePickerPopup, setShowDatePickerPopup] = useState(false);
    
    const formatDate = (date) => {
      const d = new Date(date);
      return d.toISOString().split("T")[0];
    };

    // Handle date picker confirmation
    const handleDatePickerConfirm = (startDate, endDate) => {
      setFilters((prev) => ({
        ...prev,
        dateStart: startDate,
        dateEnd: endDate,
        dateRange: {
          value: "custom",
          label: `${formatDate(startDate)} : ${formatDate(endDate)}`,
        },
      }));
      setShowDatePickerPopup(false);
    };

  const handleExcludeWeekends = (event) => {
    setFilters((prev) => ({ ...prev, excludeWeekends: event.target.checked }));
  };

  const handleChange = useCallback(
    (selectedOptions, { name }) => {
      if (name === "dateRange") {
        if (selectedOptions.value === "custom") {
          setShowDatePickerPopup(true);
        } else {
          setFilters((prev) => ({
            ...prev,
            dateRange: selectedOptions,
            dateStart: null,
            dateEnd: null,
          }));
        }
      } else {
        setFilters((prev = {}) => ({
          ...prev,
          [name]: selectedOptions,
        }));
      }
    },
    [setFilters]
  );
  
  const customStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "38px",
      height: "auto",
    }),
    option: (provided) => ({
      ...provided,
      display: "flex",
      justifyContent: "space-between",
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#e2e8f0",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      fontSize: "14px",
    }),
    valueContainer: (provided) => ({
        ...provided,
        flexWrap: "wrap",
        overflowY: "auto",
        padding: "2px 6px",
    }),
    placeholder: (provided) => ({
        ...provided,
        whiteSpace: "nowrap",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000, // Increase this value as needed
      width: "auto",
      minWidth: "275px",  // Same as control width
      maxWidth: "550px",  // 2x the control width
      left: "50%",
      transform: "translateX(-50%)",
    }),
    menuList: (provided) => ({
      ...provided,
      overflowX: "auto",  // Only add horizontal scroll if content exceeds maxWidth
    }),
  };

  const formatOptionLabel = ({ label, count }, { context }) => {
    if (context === "menu") {
      return (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <span>{label}</span>
          {count !== undefined && (
            <span style={{ color: "#aaa", paddingLeft: "5px" }}>({count})</span>
          )}
        </div>
      );
    } else {
      return label;
    }
  };

  const formattedIntents = useMemo(() => {
    if (intents) {
      return intents
        .map((intent) => ({
          value: intent.intentName,
          label: intent.intentName,
          count: intent.totalCalls,
        }))
        .sort((a, b) => b.count - a.count); // Sort descending by count
    }
  }, [intents]);

  const formattedCategories = useMemo(() => {
    if (intents) {
      const categoryCount = intents.reduce((acc, obj) => {
        const category = obj.category;
        acc[category] = (acc[category] || 0) + 1;
        return acc;
      }, {});

      return Object.entries(categoryCount)
        .map(([category, count]) => ({
          value: category,
          label: category,
          count: count,
        }))
        .sort((a, b) => b.count - a.count); // Sort descending by count
    } else {
      return [];
    }
  }, [intents]);

  return (
    <div className="mb-4 p-4 rounded-lg">
      <div className="flex space-x-8 mb-1 ml-5 mr-5">
        <div className="flex-1 relative">
        <Select
            name="dateRange"
            value={
              filters.dateRange &&
              filters.dateRange.value === "custom" &&
              filters.dateStart &&
              filters.dateEnd
                ? {
                    value: "custom",
                    label: `Custom - ${formatDate(filters.dateStart)} : ${formatDate(
                      filters.dateEnd
                    )}`,
                  }
                : filters.dateRange
            }
            onChange={(option) => handleChange(option, { name: "dateRange" })}
            options={dateOptions}
            styles={customStyles}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(56 126 252)",
              },
            })}
          />
        </div>

        <div className="flex-1 relative">
            <QueueFilter
                queues={queues}
                selectedQueues={filters.queue || []}
                onChange={(selectedQueues) =>
                    handleChange(selectedQueues, { name: "queue" })
                }
                placeholder="Select Queue"
            />
        </div>

        <div className="flex-1 relative">
          <Select
            name="initiationMethod"
            value={filters.initiationMethod}
            onChange={(option) =>
              handleChange(option, { name: "initiationMethod" })
            }
            options={initiationMethods}
            isMulti
            isClearable
            formatOptionLabel={formatOptionLabel}
            placeholder="Select Contact Origination"
            styles={customStyles}
            theme={(theme) => ({
              ...theme,
              colors: {
                ...theme.colors,
                primary: "rgb(56 126 252)",
              },
            })}
          />
        </div>

        {!miniView && (
          <>
            <div className="flex-1 relative">
              <Select
                name="category"
                value={filters.category}
                onChange={(option) =>
                  handleChange(option, { name: "category" })
                }
                options={formattedCategories}
                isClearable
                placeholder="Select Category"
                formatOptionLabel={formatOptionLabel}
                styles={customStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "rgb(56 126 252)",
                  },
                })}
              />
            </div>
            <div className="flex-1 relative">
              <Select
                name="intent"
                value={filters.intent}
                onChange={(option) => handleChange(option, { name: "intent" })}
                options={formattedIntents}
                isClearable
                placeholder="Select Intent"
                formatOptionLabel={formatOptionLabel}
                styles={customStyles}
                theme={(theme) => ({
                  ...theme,
                  colors: {
                    ...theme.colors,
                    primary: "rgb(56 126 252)",
                  },
                })}
              />
            </div>
          </>
        )}
      </div>

      <div className={`flex justify-end mt-3 ml-8 mr-5 ${filters.dateRange.value === "today" ? "invisible" : ""}`}>
        <label className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={filters.excludeWeekends}
            onChange={handleExcludeWeekends}
                      className="form-checkbox h-5 w-5  usan-primary"
          />
          <span className="text-sm">Exclude weekends</span>
        </label>
      </div>

      {/* Date Picker Popup */}
      <DatePickerPopup
        isOpen={showDatePickerPopup}
        onClose={() => setShowDatePickerPopup(false)}
        onConfirm={handleDatePickerConfirm}
        startDate={localDateStart}
        endDate={localDateEnd}
        onStartDateChange={setLocalDateStart}
        onEndDateChange={setLocalDateEnd}
      />
    </div>
  );
}

export default IntentsFilter;
